// ─────────────────────────────────────────────────────────────────────────────
// useCalendar
// Single source of truth for economic calendar data.
//
// Reads from the forex-calendar Edge Function (Gemini + cache).
//
// DEPLOY ORDER: the patched forex-calendar edge function must be deployed
// BEFORE this hook. This hook requires the `data_status` / `covers_today` /
// `safe_to_assert_no_events` fields on the response. If they are missing it
// treats the payload as untrusted and reports marketStatus "unknown" — which
// is correct-but-loud, not silently green.
//
// WHAT CHANGED vs. the previous version, and why:
//
//   1. FAILURE NO LONGER MEANS SAFE. The old catch block called
//      setMarketStatus("safe") — a dead API rendered a green SAFE TO TRADE
//      banner. There is now a fourth status, "unknown", used whenever the
//      data cannot be trusted (fetch failed, payload stale, payload's date
//      window does not cover today). Absence of data is never evidence of
//      absence of events.
//
//   2. POLLING REDUCED 3x AND GATED ON VISIBILITY. The old 5-minute interval,
//      multiplied by every open tab, is what exhausted the Gemini quota once
//      the backend cache stopped refreshing. Server cache TTL is 60 minutes,
//      so polling faster than that buys nothing. Now 15 minutes, skipped
//      entirely while the tab is hidden, with jitter to avoid synchronised
//      thundering herds across sessions.
//
//   3. STATUS TICKS INDEPENDENTLY OF FETCHING. marketStatus used to only
//      recompute inside load(), i.e. every 5 minutes, against a 15-minute
//      danger threshold — you could enter NO TRADE ZONE up to 5 minutes late.
//      A 30-second local ticker now recomputes status from already-parsed
//      events without touching the network.
//
//   4. CORRECT DST. The old month-range heuristic (months 4–10 = EDT) is
//      wrong for March 8–31 and the first week of November, stamping every
//      event one hour LATE during those windows — the app would read SAFE at
//      the moment of an 08:30 release. Offsets now come from the IANA
//      database via Intl.
//
//   5. ABORT ON UNMOUNT. In-flight requests are cancelled and state writes
//      after unmount are suppressed.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

const ET                   = "America/New_York";
const FETCH_INTERVAL_MS    = 15 * 60 * 1000;   // server cache TTL is 60m
const FETCH_JITTER_MS      = 90 * 1000;
const STATUS_TICK_MS       = 30 * 1000;
const DANGER_MINS          = 15;
const CAUTION_MINS         = 45;
const POST_FIRE_BUFFER_MIN = 5;

// ─── Timezone helpers ─────────────────────────────────────────────────────────

const ET_PARTS_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: ET, hour12: false,
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

/** Real UTC offset (in minutes) for America/New_York at a given instant. */
function etOffsetMinutes(date) {
  const parts = ET_PARTS_FMT.formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return (asUTC - date.getTime()) / 60000;
}

/**
 * Convert an ET wall-clock date + time ("2026-07-28", "08:30") to a real Date.
 * Iterates twice so the offset is resolved against the correct instant, which
 * is what makes DST transitions come out right.
 */
function etWallClockToDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const [y, mo, d] = dateStr.split("-").map(Number);
  if (!y || !mo || !d) return null;

  const [hh, mm] = (timeStr || "00:00").split(":").map(Number);
  const hour = Number.isFinite(hh) ? hh : 0;
  const min  = Number.isFinite(mm) ? mm : 0;

  const naiveUTC = Date.UTC(y, mo - 1, d, hour, min);
  let ts = naiveUTC;
  for (let i = 0; i < 2; i++) {
    ts = naiveUTC - etOffsetMinutes(new Date(ts)) * 60000;
  }
  const out = new Date(ts);
  return isNaN(out.getTime()) ? null : out;
}

function todayET() {
  return new Date().toLocaleDateString("en-CA", { timeZone: ET });
}

function dayLabelET(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", timeZone: ET,
  });
}

// ─── Normalisation ────────────────────────────────────────────────────────────

function normalizeEvent(e, todayLabel) {
  const dt = etWallClockToDate(e.date, e.time);
  if (!dt) return null;

  const dateLabel = dayLabelET(dt);

  let timeLabel = "All Day";
  if (e.time) {
    const h    = parseInt(String(e.time).split(":")[0], 10);
    const m    = parseInt(String(e.time).split(":")[1], 10) || 0;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12  = h % 12 || 12;
    timeLabel  = `${h12}:${String(m).padStart(2, "0")} ${ampm} ET`;
  }

  return {
    dt,
    dateLabel,
    isToday:  dateLabel === todayLabel,
    isAllDay: !e.time,
    time:     timeLabel,
    event:    e.title || "Unknown Event",
    impact:   String(e.impact || "Low").toLowerCase(),
    currency: "USD",
    forecast: e.forecast || "—",
    previous: e.previous || "—",
    actual:   e.actual   || null,
  };
}

// ─── Status derivation ────────────────────────────────────────────────────────

/**
 * Derive market status from today's events.
 * `trustworthy` gates the whole thing: if the underlying payload can't be
 * trusted, status is "unknown" regardless of what the (possibly empty,
 * possibly stale) event list says.
 */
function deriveStatus(todays, trustworthy, nowMs = Date.now()) {
  if (!trustworthy) return { marketStatus: "unknown", nextHighImpact: null };

  const upcomingHigh = todays
    .filter(e => e.impact === "high" && !e.isAllDay)
    .filter(e => e.dt.getTime() > nowMs - POST_FIRE_BUFFER_MIN * 60000)
    .sort((a, b) => a.dt - b.dt);

  if (upcomingHigh.length === 0) {
    return { marketStatus: "safe", nextHighImpact: null };
  }

  const next      = upcomingHigh[0];
  const minsUntil = (next.dt.getTime() - nowMs) / 60000;

  return {
    marketStatus:
      minsUntil <= DANGER_MINS  ? "danger"  :
      minsUntil <= CAUTION_MINS ? "caution" : "safe",
    nextHighImpact: { ...next, minsUntil: Math.round(minsUntil) },
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCalendar() {
  const [eventsByDay,    setEventsByDay]    = useState({});
  const [todayEvents,    setTodayEvents]    = useState([]);
  const [marketStatus,   setMarketStatus]   = useState("unknown");
  const [nextHighImpact, setNextHighImpact] = useState(null);
  const [calLoading,     setCalLoading]     = useState(true);

  // Trust signalling — consumers MUST gate any "no events / safe to trade"
  // affirmative on `dataTrustworthy`, never on todayEvents.length === 0.
  const [calStatus,       setCalStatus]       = useState("loading");
  const [calError,        setCalError]        = useState(null);
  const [dataTrustworthy, setDataTrustworthy] = useState(false);
  const [lastUpdated,     setLastUpdated]     = useState(null);
  const [coverageEnd,     setCoverageEnd]     = useState(null);

  const todaysRef      = useRef([]);
  const trustworthyRef = useRef(false);
  const mountedRef     = useRef(true);
  const abortRef       = useRef(null);
  // Mirror of lastUpdated for the visibility handler, which is bound once
  // and would otherwise read the initial null forever (refetching on every
  // tab focus).
  const lastUpdatedRef = useRef(null);

  const applyUntrusted = useCallback((status, message) => {
    if (!mountedRef.current) return;
    todaysRef.current      = [];
    trustworthyRef.current = false;
    setCalStatus(status);
    setCalError(message || null);
    setDataTrustworthy(false);
    setMarketStatus("unknown");
    setNextHighImpact(null);
  }, []);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (mountedRef.current) setCalLoading(true);

    try {
      const res = await authFetch(
        `${SUPABASE_URL}/functions/v1/forex-calendar`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        },
      );

      // The patched edge function returns 503 with data_status "unavailable"
      // when it has nothing trustworthy to serve. Parse the body either way.
      let data = null;
      try {
        data = await res.json();
      } catch {
        throw new Error(`forex-calendar ${res.status}: unparseable response`);
      }

      if (!res.ok && !data?.data_status) {
        throw new Error(`forex-calendar ${res.status}`);
      }

      const status = data?.data_status ?? null;

      // Missing trust fields = old edge function still deployed. Refuse to
      // treat the payload as authoritative rather than silently going green.
      if (status === null || typeof data?.safe_to_assert_no_events !== "boolean") {
        applyUntrusted(
          "contract_mismatch",
          "Calendar service returned an unrecognised response shape — deploy the patched forex-calendar function.",
        );
        return;
      }

      lastUpdatedRef.current = data.fetched_at || null;
      setLastUpdated(data.fetched_at || null);
      setCoverageEnd(data.window_end || null);

      const trustworthy = data.safe_to_assert_no_events === true;

      // Always parse and display whatever events came back — a stale payload
      // still holds real events worth showing. What changes is whether their
      // ABSENCE is meaningful.
      const todayDateET = todayET();
      const todayLabel  = dayLabelET(new Date());
      const raw         = Array.isArray(data.events) ? data.events : [];

      const parsed = raw
        .filter(e => e.currency === "USD" && e.date >= todayDateET)
        .map(e => normalizeEvent(e, todayLabel))
        .filter(Boolean)
        .sort((a, b) => a.dt - b.dt);

      const grouped = {};
      for (const e of parsed) {
        (grouped[e.dateLabel] ||= []).push(e);
      }

      const todays = parsed.filter(e => e.isToday);

      if (!mountedRef.current) return;

      todaysRef.current      = todays;
      trustworthyRef.current = trustworthy;

      setTodayEvents(todays);
      setEventsByDay(grouped);
      setCalStatus(status);
      setCalError(trustworthy ? null : (data.error || statusMessage(status)));
      setDataTrustworthy(trustworthy);

      const derived = deriveStatus(todays, trustworthy);
      setMarketStatus(derived.marketStatus);
      setNextHighImpact(derived.nextHighImpact);

    } catch (err) {
      if (err?.name === "AbortError") return;
      console.warn("Calendar fetch failed:", err?.message || "Unknown error");
      // Deliberately NOT clearing eventsByDay/todayEvents here: the last known
      // events are better than nothing on screen. dataTrustworthy=false is what
      // stops the UI asserting anything about them.
      applyUntrusted("unavailable", err?.message || "Calendar unavailable");
    } finally {
      if (mountedRef.current) setCalLoading(false);
    }
  }, [applyUntrusted]);

  // ── Fetch loop: visibility-gated, jittered ────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    load();

    let timer = null;
    const schedule = () => {
      const delay = FETCH_INTERVAL_MS + Math.random() * FETCH_JITTER_MS;
      timer = setTimeout(() => {
        if (!document.hidden) load();
        schedule();
      }, delay);
    };
    schedule();

    // Refresh on tab focus if the payload has gone cold, so a backgrounded
    // tab doesn't come forward showing an hour-old calendar.
    const onVisible = () => {
      if (document.hidden) return;
      const lu  = lastUpdatedRef.current;
      const age = lu ? Date.now() - new Date(lu).getTime() : Infinity;
      if (age > FETCH_INTERVAL_MS) load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // ── Status ticker: recompute locally, no network ──────────────────────────
  useEffect(() => {
    const tick = () => {
      const derived = deriveStatus(todaysRef.current, trustworthyRef.current);
      setMarketStatus(derived.marketStatus);
      setNextHighImpact(derived.nextHighImpact);
    };
    const id = setInterval(tick, STATUS_TICK_MS);
    return () => clearInterval(id);
  }, []);

  return {
    eventsByDay,
    todayEvents,
    marketStatus,      // "safe" | "caution" | "danger" | "unknown"
    nextHighImpact,
    calLoading,
    // Trust signalling
    calStatus,         // "fresh"|"cached"|"stale"|"uncovered"|"unavailable"|"contract_mismatch"|"loading"
    calError,
    dataTrustworthy,   // gate ALL "no events" affirmatives on this
    lastUpdated,
    coverageEnd,
  };
}

function statusMessage(status) {
  switch (status) {
    case "stale":
      return "Calendar data is stale — the upstream feed is failing. Verify against an external calendar before trading.";
    case "uncovered":
      return "Calendar data does not cover today. Absence of events here means nothing. Verify externally.";
    case "unavailable":
      return "Calendar unavailable. Verify against an external calendar before trading.";
    default:
      return null;
  }
}
